import React, { useEffect, useState } from "react";
import { api, formatErr, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Pin, Trash2, RotateCcw, Zap, Shield, Sunrise, MessageCircle, Search } from "lucide-react";

const STATUS_PILL = {
  active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  removed: "bg-[#DC2626]/15 text-[#FCA5A5] border-[#DC2626]/40",
};

export default function AdminWall() {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("");  // empty | active | removed
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    const url = filter ? `/admin/wall?status=${filter}` : "/admin/wall";
    const { data } = await api.get(url);
    setPosts(data);
  };
  useEffect(() => { load(); }, [filter]);

  const remove = async (p) => {
    const reason = window.prompt(`Remove this Pulse by ${p.user_name}?\n\nReason (members never see your name, only the reason):`);
    if (!reason || !reason.trim()) return;
    setBusy(p.id);
    try {
      await api.post(`/admin/wall/${p.id}/remove`, { reason: reason.trim() });
      toast.success("Post removed");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  const restore = async (p) => {
    setBusy(p.id);
    try {
      await api.post(`/admin/wall/${p.id}/restore`);
      toast.success("Post restored");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  const togglePin = async (p) => {
    setBusy(p.id);
    try {
      await api.post(`/admin/wall/${p.id}/pin`);
      toast.success(p.pinned ? "Unpinned" : "Pinned to top");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  const filtered = posts.filter((p) => {
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return (p.content_text || "").toLowerCase().includes(needle)
        || (p.user_name || "").toLowerCase().includes(needle);
  });

  return (
    <div className="space-y-4" data-testid="admin-wall-panel">
      <div className="flex items-center gap-2 flex-wrap">
        {["", "active", "removed"].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-md text-[10px] uppercase tracking-widest font-bold ${filter === s ? "bg-[#1E3A8A] text-white" : "bg-white/5 text-white/55 hover:bg-white/10"}`}
            data-testid={`admin-wall-filter-${s || "all"}`}>
            {s || "All"}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search text or operator…" className="zfd-input pl-7 text-xs py-1.5 w-64" data-testid="admin-wall-search" />
        </div>
      </div>

      {filtered.length === 0 && <div className="glass-card p-10 text-center text-white/45 text-sm">No posts match.</div>}

      <div className="space-y-3">
        {filtered.map((p) => (
          <div key={p.id} className="glass-card p-4 flex gap-4 flex-wrap" data-testid={`admin-wall-row-${p.id}`}>
            <div className="flex-1 min-w-[220px]">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-bold">{p.user_name}</span>
                <span className={`text-[9px] uppercase tracking-widest border px-1.5 py-0.5 rounded-full ${STATUS_PILL[p.status] || STATUS_PILL.active}`}>{p.status}</span>
                {p.pinned && <span className="text-[9px] uppercase tracking-widest bg-[#FBBF24]/15 text-[#FBBF24] border border-[#FBBF24]/40 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1"><Pin size={9} /> Pinned</span>}
                <span className="text-[10px] text-white/40">{new Date(p.created_at).toLocaleString()}</span>
              </div>
              <div className="text-sm text-white/85 whitespace-pre-wrap break-words">{p.content_text}</div>
              {p.has_image && (
                <img src={`${API_BASE}/wall/${p.id}/image`} alt="" loading="lazy" className="mt-2 rounded-md max-h-40 object-cover border border-white/5" onError={(e) => { e.target.style.display = "none"; }} />
              )}
              {p.removed_reason && (
                <div className="text-[11px] text-[#FCA5A5] mt-1.5">Reject reason: {p.removed_reason}</div>
              )}
              <div className="flex items-center gap-3 mt-2 text-[11px] text-white/55">
                <span className="inline-flex items-center gap-1"><Zap size={11} className="text-[#FBBF24]" /> {p.reactions?.spark || 0}</span>
                <span className="inline-flex items-center gap-1"><Shield size={11} className="text-[#93C5FD]" /> {p.reactions?.respect || 0}</span>
                <span className="inline-flex items-center gap-1"><Sunrise size={11} className="text-emerald-300" /> {p.reactions?.hope || 0}</span>
                <span className="inline-flex items-center gap-1"><MessageCircle size={11} /> {p.comments_count}</span>
                <span className="text-white/40">· Pulse {p.pulse_score}</span>
              </div>
            </div>
            <div className="flex items-start gap-1.5">
              {p.status === "active" ? (
                <>
                  <button disabled={busy === p.id} onClick={() => togglePin(p)} className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded bg-white/5 hover:bg-[#FBBF24]/20 hover:text-[#FBBF24] inline-flex items-center gap-1 disabled:opacity-40" data-testid={`admin-wall-pin-${p.id}`}>
                    <Pin size={11} /> {p.pinned ? "Unpin" : "Pin"}
                  </button>
                  <button disabled={busy === p.id} onClick={() => remove(p)} className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded bg-[#DC2626] hover:bg-red-700 text-white inline-flex items-center gap-1 disabled:opacity-40" data-testid={`admin-wall-remove-${p.id}`}>
                    <Trash2 size={11} /> Remove
                  </button>
                </>
              ) : (
                <button disabled={busy === p.id} onClick={() => restore(p)} className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white inline-flex items-center gap-1 disabled:opacity-40" data-testid={`admin-wall-restore-${p.id}`}>
                  <RotateCcw size={11} /> Restore
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
